<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("platform_statement.list") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>

                <div class="col-lg-6 col-5 text-right">
                    <base-button size="md" type="primary" @click="handleUpload">
                        <i class="fa fa-upload mr-2"></i>{{ $t("platform_statement.upload") }}
                    </base-button>
                    <base-button size="md" type="info" @click="handleSummary">
                        <i class="fa fa-chart-bar mr-2"></i>{{ $t("platform_statement.summary") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div>
                <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                    <template slot="header">
                        <div class="row">
                            <div class="col-md-2">
                                <base-input>
                                    <el-select
                                        v-model="filters.platform_id"
                                        :placeholder="$t('platform_statement.platform')"
                                        filterable
                                    >
                                        <el-option
                                            v-for="option in PlatformTableWithAll"
                                            :key="option.id"
                                            :label="option.name"
                                            :value="option.id"
                                        >
                                        </el-option>
                                    </el-select>
                                </base-input>
                            </div>

                            <div class="col-md-2">
                                <base-input
                                    v-model="filters.platform_shop_id"
                                    :placeholder="$t('platform_statement.platform_shop_id')"
                                    v-on:keyup.enter="handleSearch"
                                >
                                </base-input>
                            </div>

                            <div class="col-md-3">
                                <base-input
                                    v-model="filters.statement_number"
                                    :placeholder="$t('platform_statement.statement_number')"
                                    v-on:keyup.enter="handleSearch"
                                >
                                </base-input>
                            </div>

                            <div class="col-md-2">
                                <base-input>
                                    <flat-picker
                                        slot-scope="{ focus, blur }"
                                        @on-open="focus"
                                        @on-close="blur"
                                        :config="{ allowInput: true }"
                                        :placeholder="$t('platform_statement.from_date')"
                                        class="form-control datepicker"
                                        v-model="filters.date_from"
                                    >
                                    </flat-picker>
                                </base-input>
                            </div>

                            <div class="col-md-2">
                                <base-input>
                                    <flat-picker
                                        slot-scope="{ focus, blur }"
                                        @on-open="focus"
                                        @on-close="blur"
                                        :config="{ allowInput: true }"
                                        :placeholder="$t('platform_statement.to_date')"
                                        class="form-control datepicker"
                                        v-model="filters.date_to"
                                    >
                                    </flat-picker>
                                </base-input>
                            </div>

                            <div class="col-auto">
                                <base-button size="md" @click="handleSearch">
                                    <i class="fas fa-search mr-2"></i>{{ $t("search") }}
                                </base-button>
                                <base-button size="md" type="secondary" @click="handleReset">
                                    {{ $t("cancel") }}
                                </base-button>
                            </div>
                        </div>
                    </template>

                    <div>
                        <el-table
                            :data="queriedData"
                            row-key="id"
                            header-row-class-name="thead-light"
                            v-loading="isLoading"
                        >
                            <el-table-column
                                align="left"
                                label="ID"
                                min-width="80px"
                            >
                                <div slot-scope="{ row }" class="d-flex">
                                    <span>{{ row.id }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="left"
                                :label="$t('platform_statement.platform')"
                                min-width="100px"
                            >
                                <div slot-scope="{ row }" class="d-flex">
                                    {{ getPlatformName(row.platform_id) }}
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="left"
                                :label="$t('platform_statement.statement_number')"
                                min-width="150px"
                            >
                                <div slot-scope="{ row }" class="d-flex">
                                    <span>{{ row.statement_number }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="left"
                                :label="$t('platform_statement.statement_date')"
                                min-width="120px"
                            >
                                <div slot-scope="{ row }" class="d-flex">
                                    <span>{{ formatDate(row.statement_date) }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="right"
                                :label="$t('platform_statement.total_orders')"
                                min-width="100px"
                            >
                                <div slot-scope="{ row }" class="d-flex justify-content-end">
                                    <span>{{ formatNumber(row.total_orders) }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="right"
                                :label="$t('platform_statement.total_sales_amount')"
                                min-width="120px"
                            >
                                <div slot-scope="{ row }" class="d-flex justify-content-end">
                                    <span>{{ formatCurrency(row.total_sales_amount) }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="right"
                                :label="$t('platform_statement.net_amount')"
                                min-width="120px"
                            >
                                <div slot-scope="{ row }" class="d-flex justify-content-end">
                                    <span class="text-success font-weight-bold">{{ formatCurrency(row.net_amount) }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="left"
                                :label="$t('platform_statement.uploaded_by')"
                                min-width="100px"
                            >
                                <div slot-scope="{ row }" class="d-flex">
                                    <span>{{ row.uploaded_by_name }}</span>
                                </div>
                            </el-table-column>

                            <el-table-column
                                align="right"
                                :label="$t('actions')"
                                min-width="180px"
                            >
                                <div slot-scope="{ row }" class="d-flex justify-content-end">
                                    <base-button
                                        @click.native="handleViewDetail(row)"
                                        class="edit"
                                        type="info"
                                        size="sm"
                                        icon
                                    >
                                        <i class="fa fa-eye"></i>
                                    </base-button>

                                    <base-button
                                        @click.native="handleExport(row)"
                                        class="edit"
                                        type="success"
                                        size="sm"
                                        icon
                                    >
                                        <i class="fa fa-download"></i>
                                    </base-button>

                                    <base-button
                                        @click.native="handleDelete(row)"
                                        class="remove btn-link"
                                        type="danger"
                                        size="sm"
                                        icon
                                    >
                                        <i class="fa fa-trash text-white"></i>
                                    </base-button>
                                </div>
                            </el-table-column>
                        </el-table>
                    </div>

                    <div
                        slot="footer"
                        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
                        v-if="statements.length != 0"
                    >
                        <div class="">
                            <p class="card-category">
                                {{ $t("data") }} {{ from + 1 }} {{ $t("to") }}
                                {{ to }}
                                {{ $t("of") }}
                                {{ total }}
                            </p>
                        </div>
                        <base-pagination
                            class="pagination-no-border"
                            v-model="pagination.currentPage"
                            :per-page="pagination.perPage"
                            :total="total"
                        >
                        </base-pagination>
                    </div>
                </card>
            </div>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";

export default {
    mixins: [clientPaginationMixin],
    components: {
        flatPicker,
        BasePagination,
        RouteBreadCrumb,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    },
    computed: {
        ...mapGetters({
            statements: "PlatformStatement/getStatements",
            isLoading: "PlatformStatement/isLoading",
            total: "PlatformStatement/getTotal",
            PlatformTable: "PlatformStatement/getPlatformTable",
            PlatformTableWithAll: "PlatformStatement/getPlatformTableWithAll"
        }),
        tableData() {
            return this.statements;
        }
    },
    data() {
        return {
            propsToSearch: ["statement_number", "platform_shop_id"],
            filters: {
                platform_id: 0,
                platform_shop_id: "",
                statement_number: "",
                date_from: "",
                date_to: ""
            }
        };
    },
    mounted() {
        this.filters.date_from = moment().add(-30, "days").format("YYYY-MM-DD");
        this.filters.date_to = moment().format("YYYY-MM-DD");
        this.handleSearch();
    },
    methods: {
        ...mapActions({
            listStatements: "PlatformStatement/listStatements",
            exportStatement: "PlatformStatement/exportStatement",
            deleteStatement: "PlatformStatement/deleteStatement"
        }),
        handleSearch() {
            const params = {
                page: this.pagination.currentPage,
                limit: this.pagination.perPage,
                filters: {
                    ...this.filters,
                    platform_id: this.filters.platform_id === 0 ? "" : this.filters.platform_id
                }
            };
            this.listStatements(params);
        },
        handleReset() {
            this.filters = {
                platform_id: 0,
                platform_shop_id: "",
                statement_number: "",
                date_from: moment().add(-30, "days").format("YYYY-MM-DD"),
                date_to: moment().format("YYYY-MM-DD")
            };
            this.handleSearch();
        },
        handleUpload() {
            this.$router.push("/backoffice/platform-statement/upload");
        },
        handleSummary() {
            this.$router.push("/backoffice/platform-statement/summary");
        },
        handleViewDetail(row) {
            this.$router.push(`/backoffice/platform-statement/${row.id}`);
        },
        async handleExport(row) {
            try {
                const response = await this.exportStatement(row.id);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `statement_${row.statement_number}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                swal.fire({
                    title: this.$t("platform_statement.export_fail"),
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
            }
        },
        handleDelete(row) {
            swal.fire({
                title: this.$t("platform_statement.delete_confirm"),
                text: this.$t("platform_statement.delete_confirm_text"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonClass: "btn btn-success btn-fill",
                cancelButtonClass: "btn btn-danger btn-fill",
                confirmButtonText: this.$t("yes"),
                cancelButtonText: this.$t("no"),
                buttonsStyling: false
            }).then(result => {
                if (result.value) {
                    this.deleteStatement(row.id)
                        .then(() => {
                            swal.fire({
                                title: this.$t("platform_statement.delete_success"),
                                icon: "success",
                                confirmButtonClass: "btn btn-success btn-fill",
                                buttonsStyling: false
                            });
                            this.handleSearch();
                        })
                        .catch(err => {
                            console.log(err);
                        });
                }
            });
        },
        getPlatformName(platformId) {
            const platform = this.PlatformTable.find(p => p.id === platformId);
            return platform ? platform.name : "-";
        },
        formatCurrency(amount) {
            return "฿" + parseFloat(amount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        formatNumber(num) {
            return parseInt(num || 0).toLocaleString();
        },
        formatDate(date) {
            return date ? moment(date).format("YYYY-MM-DD") : "-";
        }
    }
};
</script>

<style>
.no-border-card .card-footer {
    border-top: 0;
}
</style>
